import { ArrowRightLeft, X } from "lucide-react";
import { useState } from "react";

import type { Empleado, Equipo } from "@/lib/api";

/**
 * Transferir un equipo asignado a otro usuario. En el backend es una baja
 * lógica en el origen (queda rastro en "Bajas de asignación", solo admin) y
 * un alta del mismo equipo en el destino: ambas responsivas pasan a 🟡.
 */
export function TransferirEquipoModal({
  equipo,
  nombre,
  empleados,
  pending,
  onCancel,
  onConfirm,
}: {
  equipo: Equipo;
  nombre: string;
  empleados: Empleado[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (destino: string, motivo: string) => void;
}) {
  const [destino, setDestino] = useState("");
  const [motivo, setMotivo] = useState("");

  const candidatos = empleados.filter((e) => e.email !== equipo.empleado_email);
  const empresas = [...new Set(candidatos.map((e) => e.empresa))];
  const descripcion = [equipo.marca, equipo.modelo].filter(Boolean).join(" ");
  const destinoSel = candidatos.find((e) => e.email === destino);
  const cambiaEmpresa = !!destinoSel && destinoSel.empresa !== equipo.empresa;

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transferir-titulo"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !pending) onCancel();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="transferir-titulo" className="text-base font-semibold text-slate-900">
              Transferir {equipo.tipo} de {nombre}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {descripcion || "Sin marca/modelo"}
              {equipo.numero_serie && (
                <>
                  {" "}
                  · Serie <span className="tabular-nums">{equipo.numero_serie}</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="text-slate-400 hover:text-slate-700 p-1 disabled:opacity-50"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Usuario destino</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm block w-full"
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            autoFocus
          >
            <option value="">— Selecciona —</option>
            {empresas.map((emp) => (
              <optgroup key={emp} label={emp}>
                {candidatos
                  .filter((e) => e.empresa === emp)
                  .map((e) => (
                    <option key={e.email} value={e.email}>
                      {e.nombre ?? e.email}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>

        <p className="text-xs text-slate-600">
          El equipo se quita de la lista de <b>{nombre}</b> (baja lógica, con rastro) y se asigna
          a <b>{destinoSel ? (destinoSel.nombre ?? destinoSel.email) : "…"}</b>. Las responsivas de
          ambos quedan por actualizar (🟡).
          {cambiaEmpresa && (
            <>
              {" "}
              <span className="text-amber-700">
                El equipo pasará a la empresa del destino ({destinoSel?.empresa}).
              </span>
            </>
          )}
        </p>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">
            Motivo / observaciones <span className="font-normal text-slate-400">(opcional)</span>
          </span>
          <textarea
            className="mt-1 border rounded px-2 py-1.5 text-sm block w-full"
            rows={2}
            maxLength={300}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. cambio de equipo; el anterior se reasigna…"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!destino || pending}
            onClick={() => destino && onConfirm(destino, motivo)}
            className="inline-flex items-center gap-2 text-white rounded px-4 py-2 text-sm disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
          >
            <ArrowRightLeft className="w-4 h-4" />
            {pending ? "Transfiriendo…" : "Transferir"}
          </button>
        </div>
      </div>
    </div>
  );
}
